import { sortName } from '@/helpers/Sorting.helpers'
import { convertEnumValue } from '@/helpers/Enum.helpers'
import { buildInFilterPredicates } from '@/contexts/shared/filters-predicate/FiltersPredicate.helpers'
import { hasUndefinedQuota } from '@/contexts/quotas/Quotas.helpers'
import { PERIMETER_TYPE, TAG_TYPE } from '@/contexts/perimeters/Perimeters.constants'
import type { DatatableGroupByGroup } from '@/components/NewDesignSystem/NdsDatatable/NdsDatatable.interfaces'
import {
  type PerimeterTreeItem,
  PerimetersGroupBy,
  PerimeterTypeWithTag,
  type PerimetersGroupedFilter,
  type PerimeterTreeOrderingContext,
  type PerimeterTreePerimeterItem,
  type PerimeterTreePerimeterSelectOption,
  type PerimeterTreeSelectOption,
  type PerimeterTreeTagItem,
  type PerimeterTreeTagSelectOption,
} from '@/contexts/perimeters/Perimeters.interfaces'
import type { Quota } from '@/contexts/quotas/Quotas.interfaces'

export const buildGroupByScopeFilter = (groups: DatatableGroupByGroup[]): Partial<PerimetersGroupedFilter> => {
  const [group] = groups
  const groupBy = convertEnumValue(group?.type, PerimetersGroupBy)

  if (!groupBy) {
    return {}
  }

  switch (groupBy) {
    case PerimetersGroupBy.TYPE: {
      const type = convertEnumValue(typeof group.value === 'string' ? group.value : undefined, PerimeterTypeWithTag)

      return type ? { types: buildInFilterPredicates([type]) } : {}
    }
  }
}

const toTagOption = (tag: PerimeterTreeTagItem): PerimeterTreeTagSelectOption => ({
  ...tag,
  id: tag.db_id,
  perimeterId: tag.perimeter_id,
  name: tag.tag ?? tag.name ?? tag.identifier,
  type: TAG_TYPE,
})

const toPerimeterOption = (perimeter: PerimeterTreePerimeterItem): PerimeterTreePerimeterSelectOption => ({
  ...perimeter,
  id: perimeter.db_id,
  perimeterId: perimeter.db_id,
  name: perimeter.name ?? perimeter.identifier,
  type: PERIMETER_TYPE,
})

const findPrimaryRootTag = (
  tags: PerimeterTreeTagSelectOption[],
  context: PerimeterTreeOrderingContext
): PerimeterTreeTagSelectOption | undefined => {
  if (context.isGlobalPiloteOrAuditor) {
    return tags.find((tag) => tag.tag === context.customerName)
  }

  return tags.find((tag) => tag.id === context.localTagId)
}

const findSecondaryRootTags = (
  tags: PerimeterTreeTagSelectOption[],
  customerName: string
): PerimeterTreeTagSelectOption[] => {
  const childTagIds = new Set(tags.flatMap((tag) => tag.children_.map((child) => child.child_id)))

  return tags.filter((tag) => tag.tag !== customerName && (!tag.parentId || !childTagIds.has(tag.id)))
}

/** Preserves the legacy `tagsDfs` depth-first order without depending on the stateful TagMixin. */
const getTagsDepthFirst = (
  tag: PerimeterTreeTagSelectOption | undefined,
  tagsById: Map<number, PerimeterTreeTagSelectOption>,
  appendedTagIds: Set<number>
): PerimeterTreeTagSelectOption[] => {
  if (!tag || appendedTagIds.has(tag.id)) {
    return []
  }

  // A tag can be referenced by more than one root; append it once and guard against malformed cycles.
  appendedTagIds.add(tag.id)

  return [
    tag,
    ...tag.children_.flatMap((child) => getTagsDepthFirst(tagsById.get(child.child_id), tagsById, appendedTagIds)),
  ]
}

const getTagsInLegacyOrder = (
  tags: PerimeterTreeTagItem[],
  context: PerimeterTreeOrderingContext
): PerimeterTreeTagSelectOption[] => {
  const tagOptions = tags.map(toTagOption)
  const tagsById = new Map(tagOptions.map((tag) => [tag.id, tag]))
  const appendedTagIds = new Set<number>()
  const rootTags = [findPrimaryRootTag(tagOptions, context), ...findSecondaryRootTags(tagOptions, context.customerName)]

  return rootTags.flatMap((rootTag) => getTagsDepthFirst(rootTag, tagsById, appendedTagIds))
}

const getPerimetersInLegacyOrder = (perimeters: PerimeterTreePerimeterItem[]): PerimeterTreeSelectOption[] =>
  perimeters.map(toPerimeterOption).sort(sortName)

/**
 * Mirrors the legacy TagMixin ordering so every perimeter/tag picker presents a stable order:
 * the primary tag tree comes first, followed by secondary tag trees, then perimeters sorted by name.
 */
export const formatPerimeterTreeSelectOptions = (
  tags: PerimeterTreeTagItem[],
  perimeters: PerimeterTreePerimeterItem[],
  context: PerimeterTreeOrderingContext
): PerimeterTreeSelectOption[] => [...getTagsInLegacyOrder(tags, context), ...getPerimetersInLegacyOrder(perimeters)]

const isTagItem = (item: PerimeterTreeItem): item is PerimeterTreeTagItem => item.type === TAG_TYPE

const toSelectOption = (item: PerimeterTreeItem): PerimeterTreeSelectOption =>
  isTagItem(item) ? toTagOption(item) : toPerimeterOption(item)

export const buildPerimeterTreeSelectOptions = (items: PerimeterTreeItem[]): PerimeterTreeSelectOption[] => {
  const itemIds = new Set(items.map((item) => item.id))
  // The parent can be missing from the items, like the "My perimeters" virtual node that is not searchable
  const isTopLevelItem = (item: PerimeterTreeItem): boolean => !item.parentId || !itemIds.has(item.parentId)

  // Grouped once: filtering the items for each node is too slow on large trees
  const childrenByParentId = new Map<string | null, PerimeterTreeItem[]>()

  for (const item of items) {
    childrenByParentId.set(item.parentId, [...(childrenByParentId.get(item.parentId) ?? []), item])
  }

  const toTreeSelectOption = (item: PerimeterTreeItem): PerimeterTreeSelectOption => ({
    ...toSelectOption(item),
    children: (childrenByParentId.get(item.id) ?? []).map(toTreeSelectOption),
  })

  return items.filter(isTopLevelItem).map(toTreeSelectOption)
}

export const flattenPerimeterTreeSelectOptions = (options: PerimeterTreeSelectOption[]): PerimeterTreeSelectOption[] =>
  options.flatMap((option) => [option, ...flattenPerimeterTreeSelectOptions(option.children ?? [])])

export const findFocusedPerimeterTreeSelectOption = ({
  options,
  focusedNodeId,
  pickedOption,
}: {
  options: PerimeterTreeSelectOption[]
  focusedNodeId: string | null
  pickedOption: PerimeterTreeSelectOption | null
}): PerimeterTreeSelectOption | null => {
  const focusedNodeOptions = flattenPerimeterTreeSelectOptions(options).filter(
    (option) => `${option.type}${option.db_id}` === focusedNodeId
  )

  return (
    focusedNodeOptions.find((option) => option.parentId === pickedOption?.parentId) ?? focusedNodeOptions[0] ?? null
  )
}

export const canCreateInternalPerimeter = ({
  isGlobalPilote,
  perimetersQuota,
}: {
  isGlobalPilote: boolean
  perimetersQuota: Quota | null
}): boolean => {
  if (isGlobalPilote) {
    return true
  }

  return perimetersQuota !== null && hasUndefinedQuota(perimetersQuota)
}
